import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';

import { SettingsService } from '../../utility/services/settings.service';

import { ICollection, ITag, sortByName } from '@ml/common';

interface TagFilterNode {
  key: string;
  label: string;
  tagIds: number[];
  isToggleable: boolean;
  children: TagFilterNode[];
}

export interface IFloorplanTagFilterState {
  SelectedTagIds: number[];
  SelectedNodeKeys: string[];
}

@Component({
    selector: 'tag-filter',
    templateUrl: './tag-filter.component.html',
    styleUrls: ['./tag-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TagFilterComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() tags: ITag[] = [];
  @Input() collections: ICollection[] = [];
  @Input() initialSelectedTags: ITag[] = [];
  @Input() initialSelectedNodeKeys: string[] = [];
  @Input() titleSettingName: string;
  @Input() placeholderSettingName: string;
  @Output() selectedTagsChange = new EventEmitter<ITag[]>();
  @Output() selectedTagFilterGroupsChange = new EventEmitter<number[][]>();
  @Output() tagFilterStateChange = new EventEmitter<IFloorplanTagFilterState>();
  selectedTags: ITag[] = [];
  availableTags: ITag[] = [];
  isEnabled = false;
  titleText = 'Tag Filter';
  placeholderText = 'Select a tag';
  selectedTag: ITag;
  tagFilterCategories: TagFilterNode[] = [];
  private selectedNodeKeys = new Set<string>();
  private allToggleableNodes: TagFilterNode[] = [];

  constructor(private settingsService: SettingsService) {}

  ngOnInit() {
    this.selectedTags = this.initialSelectedTags;
    this.isEnabled = !!this.selectedTags.length || !!this.selectedNodeKeys.size;
    this.availableTags = this.availableTags.filter(x => !this.selectedTags.includes(x));
    if (this.titleSettingName)
      this.titleText = this.settingsService.getByUniqueName(this.titleSettingName);
    if (this.placeholderSettingName)
      this.placeholderText = this.settingsService.getByUniqueName(this.placeholderSettingName);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tags?.currentValue || changes.collections?.currentValue) {
      this.tagFilterCategories = this.collections
        .filter(c => c.ParentCollectionId === null && this.getEffectiveTagIds(c).length > 0)
        .map(category => this.buildTagFilterNode(category, false));
      this.allToggleableNodes = this.flattenToggleableNodes(this.tagFilterCategories);

      const collectionTagIds = new Set(this.collections.flatMap(c => c.TagIds ?? []));
      this.availableTags = [...this.tags]
        .filter(t => !collectionTagIds.has(t.TagId))
        .sort(sortByName);
      this.selectedTags = [];
      this.isEnabled = false;

      const isFirstChange =
        (changes.tags?.firstChange ?? true) && (changes.collections?.firstChange ?? true);
      if (isFirstChange && this.initialSelectedNodeKeys.length) {
        const validKeys = new Set(this.allToggleableNodes.map(n => n.key));
        this.selectedNodeKeys = new Set(
          this.initialSelectedNodeKeys.filter(key => validKeys.has(key))
        );
      } else {
        this.selectedNodeKeys = new Set<string>();
      }
    }
  }

  ngAfterViewInit() {
    // reflect any restored selection (dropdown tags + collection toggles) in the actual filter
    this.emitSelectedTagFilterGroups();
  }

  handleEnabledToggle(isEnabled: boolean) {
    this.isEnabled = isEnabled;
    this.selectedTagsChange.emit(isEnabled ? this.selectedTags : []);
    this.emitSelectedTagFilterGroups();
  }

  handleTagSelect(change: MatSelectChange) {
    const tag = change.value as ITag;
    this.selectedTags.push(tag);
    this.selectedTags.sort(sortByName);
    this.availableTags = this.availableTags.filter(x => x.TagId !== tag.TagId);

    this.isEnabled = true;
    this.selectedTagsChange.emit(this.selectedTags);
    this.emitSelectedTagFilterGroups();
  }

  handleTagRemove(tag: ITag) {
    this.selectedTags = this.selectedTags.filter(x => x.TagId !== tag.TagId);
    this.availableTags.push(tag);
    this.availableTags.sort(sortByName);

    if (!this.selectedTags.length) this.isEnabled = false;
    this.selectedTagsChange.emit(this.selectedTags);
    this.emitSelectedTagFilterGroups();
    // mainly necessary so that you are able to reselect that same tag
    this.selectedTag = null;
  }

  handleNodeToggle(node: TagFilterNode, isOn: boolean) {
    const descendantKeys = this.getAllDescendantKeys(node);
    if (isOn) {
      this.selectedNodeKeys.add(node.key);
      descendantKeys.forEach(key => this.selectedNodeKeys.add(key));
    } else {
      this.selectedNodeKeys.delete(node.key);
      descendantKeys.forEach(key => this.selectedNodeKeys.delete(key));
    }

    const path = this.findNodeAndAncestors(this.tagFilterCategories, node.key) ?? [];
    const ancestorsClosestFirst = path.slice(0, -1).reverse();
    for (const ancestor of ancestorsClosestFirst) {
      const allChildrenSelected = ancestor.children.every(c => this.selectedNodeKeys.has(c.key));
      if (allChildrenSelected) this.selectedNodeKeys.add(ancestor.key);
      else this.selectedNodeKeys.delete(ancestor.key);
    }

    if (this.selectedNodeKeys.size) this.isEnabled = true;
    else if (!this.selectedTags.length) this.isEnabled = false;

    this.emitSelectedTagFilterGroups();
  }

  isNodeSelected(node: TagFilterNode): boolean {
    return this.isEnabled && this.selectedNodeKeys.has(node.key);
  }

  private emitSelectedTagFilterGroups() {
    const dropdownGroups = this.isEnabled ? this.selectedTags.map(t => [t.TagId]) : [];

    // tags/collections selected within the same top-level collection are OR'd together
    // (union into one group); different top-level collections still AND against each other
    const tagIdsByRootCategoryKey = new Map<string, Set<number>>();
    if (this.isEnabled) {
      for (const node of this.allToggleableNodes) {
        if (!this.selectedNodeKeys.has(node.key)) continue;

        const path = this.findNodeAndAncestors(this.tagFilterCategories, node.key) ?? [];
        const rootKey = (path[0] ?? node).key;
        const tagIds = tagIdsByRootCategoryKey.get(rootKey) ?? new Set<number>();
        node.tagIds.forEach(id => tagIds.add(id));
        tagIdsByRootCategoryKey.set(rootKey, tagIds);
      }
    }
    const toggleGroups = Array.from(tagIdsByRootCategoryKey.values()).map(tagIds => [...tagIds]);

    this.selectedTagFilterGroupsChange.emit([...dropdownGroups, ...toggleGroups]);
    this.tagFilterStateChange.emit({
      SelectedTagIds: this.selectedTags.map(t => t.TagId),
      SelectedNodeKeys: [...this.selectedNodeKeys]
    });
  }

  private getAllDescendantKeys(node: TagFilterNode): string[] {
    return node.children.flatMap(child => [child.key, ...this.getAllDescendantKeys(child)]);
  }

  private findNodeAndAncestors(
    nodes: TagFilterNode[],
    key: string,
    ancestors: TagFilterNode[] = []
  ): TagFilterNode[] | null {
    for (const node of nodes) {
      if (node.key === key) return [...ancestors, node];
      const found = this.findNodeAndAncestors(node.children, key, [...ancestors, node]);
      if (found) return found;
    }
    return null;
  }

  private getEffectiveTagIds(collection: ICollection): number[] {
    const childTagIds = this.collections
      .filter(c => c.ParentCollectionId === collection.CollectionId)
      .flatMap(c => this.getEffectiveTagIds(c));
    return [...new Set([...(collection.TagIds ?? []), ...childTagIds])];
  }

  private buildTagFilterNode(collection: ICollection, isToggleable: boolean): TagFilterNode {
    const ownTagNodes: TagFilterNode[] = (collection.Tags ?? []).map(tag => ({
      key: `tag-${tag.TagId}`,
      label: tag.Name,
      tagIds: [tag.TagId],
      isToggleable: true,
      children: []
    }));

    const childCollectionNodes: TagFilterNode[] = this.collections
      .filter(c => c.ParentCollectionId === collection.CollectionId)
      .map(child => this.buildTagFilterNode(child, true));

    return {
      key: `collection-${collection.CollectionId}`,
      label: collection.Title,
      tagIds: this.getEffectiveTagIds(collection),
      isToggleable,
      children: [...ownTagNodes, ...childCollectionNodes]
    };
  }

  private flattenToggleableNodes(nodes: TagFilterNode[]): TagFilterNode[] {
    return nodes.flatMap(n => [
      ...(n.isToggleable ? [n] : []),
      ...this.flattenToggleableNodes(n.children)
    ]);
  }

  trackByTagId(_, tag: ITag) {
    return tag.TagId;
  }
}
